import { ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, CaretDown, Gear, GearSixIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/ds/components/tooltip";
import { InstructionsDialog } from "@/zapi-frontend/shared/ui/InstructionsModal/InstructionsDialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@libs/zapi-ui/shadcn";
import { AppButton, InputLabel, FigmaTypography } from "@libs/zapi-ui/custom";
import { InstructionsModal } from "@/zapi-frontend/shared/ui/InstructionsModal/InstructionsModal";
import { useProjectTemplates } from "../model/useProjectTemplates";

interface SelectionDropdownProps {
  options: { value: string; label: string; component?: ReactNode }[];
  placeholder?: string;
  value: string | undefined;
  onChange: (value: string) => void;
  className?: string;
  error?: string;
  disabled?: boolean;
  label?: string;
  isOptional?: boolean;
  showSettings?: boolean;
  onSettingsClick?: () => void;
}

function SelectionDropdown({
  options,
  placeholder = "Select option...",
  value,
  onChange,
  className,
  error,
  disabled,
  label,
  isOptional,
  showSettings = false,
  onSettingsClick
}: SelectionDropdownProps) {
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="flex flex-col relative min-w-0">
      {label && (
        <InputLabel isOptional={isOptional} className="mb-[9px] text-innoveraZinc-900">
          {label}
        </InputLabel>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <AppButton
            data-testid="analysis-dropdown-trigger"
            appearance="outlined"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between font-normal",
              value ? "text-zinc-900" : "text-innoveraZinc-400 hover:text-innoveraZinc-400",
              !!error && "border-innoveraRed-400 hover:ring-innoveraRed-50 focus-visible:ring-innoveraRed-50",
              className
            )}
            size="default"
            disabled={disabled}
          >
            <span className="truncate min-w-0 flex-1 text-left">{selectedOption?.label || placeholder}</span>
            {showSettings && value ? (
              <button
                type="button"
                data-testid="analysis-dropdown-settings-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSettingsClick?.();
                }}
                className="flex items-center gap-2 ml-2 pl-2 shrink-0 border-l border-innoveraZinc-200"
              >
                <Gear className="w-4 h-4" />
                <span className="text-[14px]">Analysis Settings</span>
                <CaretDown className="w-4 h-4" />
              </button>
            ) : (
              <CaretDown className="opacity-50 shrink-0 ml-2" />
            )}
          </AppButton>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto min-w-(--radix-popover-trigger-width) max-w-[400px] p-0"
          sideOffset={4}
        >
          <Command>
            <CommandList onWheel={(e) => e.stopPropagation()}>
              <CommandEmpty>No option found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = value === option.value;
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      data-testid={`analysis-dropdown-${option.value}-item`}
                      onSelect={(currentValue) => {
                        onChange(currentValue === value ? "" : currentValue);
                        setOpen(false);
                      }}
                      className={cn(
                        "min-w-0 hover:bg-[rgba(255,96,62,0.05)]",
                        isSelected && "rounded-md border border-[#FF603E] bg-[rgba(255,96,62,0.05)]"
                      )}
                    >
                      <span className="flex-1 whitespace-normal wrap-break-word">{option.component || option.label}</span>
                      <Check className={cn("ml-auto shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {error && <p className="absolute bottom-[-14px] left-1 text-innoveraRed-400 text-[10px]">{error}</p>}
    </div>
  );
}

interface AnalysisDropdownProps {
  value: string | undefined;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  projectInstructions?: string;
  chaptersInstructions?: Record<string, string>;
  presentationPrompt?: string;
  onInstructionsChange?: (projectInstructions: string, chaptersInstructions: Record<string, string>) => void;
  onPresentationPromptChange?: (value: string) => void;
  appearance?: "design-system";
}

export function AnalysisDropdown({
  value,
  onChange,
  error,
  disabled = false,
  projectInstructions = "",
  chaptersInstructions = {},
  presentationPrompt = "",
  onInstructionsChange,
  onPresentationPromptChange,
  appearance
}: AnalysisDropdownProps) {
  const { t } = useTranslation(["createProject", "projectHome"]);
  const { projectTemplates, isProjectTemplatesLoading } = useProjectTemplates();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const templateId = useId();

  const selectedTemplate = projectTemplates.find((t) => t.slug === value);

  if (appearance === "design-system") {
    const items = projectTemplates.map((template) => ({ value: template.slug, label: template.name }));
    const instructionsLabel = t("projectHome:instructionsDialog.title");
    return (
      <>
        <Field data-invalid={!!error || undefined}>
          <FieldLabel htmlFor={templateId}>{t("newProject.analysisType")}</FieldLabel>
          <div className="flex gap-2">
            <Select
              items={items}
              value={value || null}
              onValueChange={(next) => typeof next === "string" && onChange(next)}
              disabled={disabled || isProjectTemplatesLoading}
            >
              <SelectTrigger
                id={templateId}
                aria-invalid={!!error || undefined}
                className="min-w-0 flex-1"
                data-testid="analysis-dropdown-trigger"
              >
                <SelectValue
                  placeholder={
                    isProjectTemplatesLoading ? t("newProject.loading") : t("newProject.analysisTypePlaceholder")
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {projectTemplates.map((template) => (
                  <SelectItem
                    key={template.slug}
                    value={template.slug}
                    data-testid={`analysis-dropdown-${template.slug}-item`}
                  >
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTemplate && (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      aria-label={instructionsLabel}
                      onClick={() => setIsModalOpen(true)}
                      data-testid="analysis-dropdown-settings-button"
                    />
                  }
                >
                  <GearSixIcon />
                </TooltipTrigger>
                <TooltipContent>{instructionsLabel}</TooltipContent>
              </Tooltip>
            )}
          </div>
          {selectedTemplate?.description && <FieldDescription>{selectedTemplate.description}</FieldDescription>}
          {error && <FieldError>{error}</FieldError>}
        </Field>

        <InstructionsDialog
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          chapters={selectedTemplate?.projectTemplateChapters ?? []}
          projectInstructions={projectInstructions}
          chaptersInstructions={chaptersInstructions}
          presentationPrompt={presentationPrompt}
          onProjectInstructionsChange={(pi, ci) => onInstructionsChange?.(pi, ci)}
          onPresentationPromptChange={(val) => onPresentationPromptChange?.(val)}
        />
      </>
    );
  }

  const options = projectTemplates.map((template) => ({
    value: template.slug,
    label: template.name,
    component: (
      <div className={cn("flex flex-col", template.description && "gap-2")}>
        <FigmaTypography variant="label-2">{template.name}</FigmaTypography>
        {template.description && (
          <FigmaTypography variant="body-3" color="zinc700">
            {template.description}
          </FigmaTypography>
        )}
      </div>
    )
  }));

  return (
    <>
      <SelectionDropdown
        label="What type of analysis are you doing?*"
        disabled={disabled || isProjectTemplatesLoading}
        options={options}
        placeholder={isProjectTemplatesLoading ? "Loading..." : "Select"}
        value={value}
        onChange={onChange}
        error={error}
        showSettings={true}
        onSettingsClick={() => setIsModalOpen(true)}
      />

      <InstructionsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        chapters={selectedTemplate?.projectTemplateChapters ?? []}
        projectInstructions={projectInstructions}
        chaptersInstructions={chaptersInstructions}
        presentationPrompt={presentationPrompt}
        onProjectInstructionsChange={(pi, ci) => onInstructionsChange?.(pi, ci)}
        onPresentationPromptChange={(val) => onPresentationPromptChange?.(val)}
      />
    </>
  );
}
